import React, { useCallback, useEffect, useRef, useState } from 'react'
import { CircularProgress, Fade, Grid, Modal, Typography, Backdrop, Dialog, DialogContent, DialogActions, DialogContentText, Snackbar } from '@material-ui/core'
import { Alert } from '@material-ui/lab'
import { useArquivoService } from 'hooks/useArquivoService'
import Webcam from 'react-webcam'
import PhotoCamera from '@material-ui/icons/PhotoCamera'
import CloudUploadIcon from '@material-ui/icons/CloudUpload'
import Close from '@material-ui/icons/Close'
import useStyles from './styles'
import { CustomButton } from '../CustomButton'

const videoConstraints = {
  width: 1280,
  height: 720,
  facingMode: 'user',
}

export const FileManager = (props) => {
  const classes = useStyles()
  const arquivoService = useArquivoService()
  const { rotaUpload, rotaLoadImagem, setArquivosPai, arquivosIniciais, tipos, disabledButton = false, center } = props

  const webcamRef = useRef(null)
  const fileInputRef = useRef(null)

  const [idsArquivos, setIdsArquivos] = useState([])
  const [arquivos, setArquivos] = useState([])
  const [processandoArquivos, setProcessandoArquivos] = useState(false)
  const [alertaErroTipoArquivo, setAlertaErroTipoArquivo] = useState(false)
  const [idArquivoDeletar, setIdAquivoDeletar] = useState(null)
  const [deletarOpen, setDeletarOpen] = useState(false)
  const [cameraOpen, setCameraOpen] = useState(false)
  const [cameraImageData, setCameraImageData] = useState(null)

  const lastInitialRef = useRef([])

  // Sincroniza arquivosIniciais com o estado local somente quando os valores da prop mudam de verdade
  useEffect(() => {
    const isDifferent = arquivosIniciais.length !== lastInitialRef.current.length || arquivosIniciais.some((val, idx) => val !== lastInitialRef.current[idx])

    if (isDifferent) {
      setIdsArquivos(arquivosIniciais)
      lastInitialRef.current = arquivosIniciais
    }
  }, [arquivosIniciais])

  // Notifica o componente pai sempre que a lista de ids locais for alterada
  useEffect(() => {
    setArquivosPai([...idsArquivos])
  }, [idsArquivos, setArquivosPai])

  const fetchArquivosAPI = useCallback(
    async (ids) => {
      setProcessandoArquivos(true)
      try {
        const results = await Promise.all(
          ids.map(async (idArq) => {
            try {
              const response = await arquivoService.listarArquivo(rotaLoadImagem, idArq)
              if (response) {
                const { arquivo, mimetype, nomeOriginal } = response
                return { data: arquivo.data, id: idArq, mimetype, nomeOriginal }
              }
            } catch (error) {
              console.error(`Erro ao carregar arquivo individual ${idArq}:`, error)
            }
            return null
          })
        )
        return results.filter(Boolean)
      } catch (error) {
        console.error('Erro ao buscar arquivos do FTP:', error)
        return []
      } finally {
        setProcessandoArquivos(false)
      }
    },
    [rotaLoadImagem]
  )

  const fetchArquivos = useCallback(async () => {
    const response = await fetchArquivosAPI(idsArquivos)
    setArquivos(response)
  }, [fetchArquivosAPI, idsArquivos])

  // Busca os arquivos da API sempre que os IDs mudarem
  useEffect(() => {
    fetchArquivos()
  }, [idsArquivos, fetchArquivos])

  const arrayBufferToBase64 = useCallback((buffer) => {
    let binary = ''
    const bytes = [].slice.call(new Uint8Array(buffer))
    bytes.forEach((b) => (binary += String.fromCharCode(b)))
    return window.btoa(binary)
  }, [])

  const getRawImageContent = useCallback(
    (data, mimetype) => {
      const b64 = arrayBufferToBase64(data)
      return `data:${mimetype};base64,${b64}`
    },
    [arrayBufferToBase64]
  )

  const getSrc = useCallback(
    (data, mimetype) => {
      const mime = mimetype?.toLowerCase()
      if (['image/png', 'image/jpg', 'image/jpeg', 'image/gif'].includes(mime)) {
        return getRawImageContent(data, mime)
      }
      return '/images/pdf_image.png'
    },
    [getRawImageContent]
  )

  const dataURItoBlob = useCallback((dataURI) => {
    const byteString = atob(dataURI.split(',')[1])
    const mimeString = dataURI.split(',')[0].split(':')[1].split(';')[0]
    const ab = new ArrayBuffer(byteString.length)
    const ia = new Uint8Array(ab)
    for (let i = 0; i < byteString.length; i += 1) {
      ia[i] = byteString.charCodeAt(i)
    }
    return new Blob([ab], { type: mimeString })
  }, [])

  const handleEnviarArquivoComputador = useCallback(
    async (event) => {
      const file = event.target.files?.[0]
      if (!file) return

      try {
        setProcessandoArquivos(true)

        // Se não for múltiplo e já tiver um arquivo, deleta o anterior primeiro
        if (props.multiple === false && idsArquivos.length > 0) {
          try {
            await arquivoService.deletarArquivo(rotaUpload, idsArquivos[0])
          } catch (e) {
            console.error('Erro ao deletar arquivo anterior em modo single:', e)
          }
        }

        const formData = new FormData()
        formData.append('file', file, file.name)

        const idArquivo = await arquivoService.uploadArquivo(rotaUpload, formData)
        if (props.multiple === false) {
          setIdsArquivos([idArquivo])
        } else {
          setIdsArquivos((prevState) => [...prevState, idArquivo])
        }
      } catch (error) {
        console.error('Erro ao fazer upload do arquivo:', error)
        if (error?.response?.status === 415) {
          setAlertaErroTipoArquivo(true)
        }
      } finally {
        setProcessandoArquivos(false)
        if (event.target) {
          event.target.value = ''
        }
      }
    },
    [rotaUpload, props.multiple, idsArquivos]
  )

  const handleOpenDeletar = useCallback((event, idArquivo) => {
    setIdAquivoDeletar(idArquivo)
    setDeletarOpen(true)
  }, [])

  const handleFecharDeletar = useCallback(() => {
    setDeletarOpen(false)
    setIdAquivoDeletar(null)
  }, [])

  const handleOpenCamera = useCallback(() => {
    setCameraOpen(true)
  }, [])

  const handleCloseCamera = useCallback(() => {
    setCameraOpen(false)
    setCameraImageData(null)
  }, [])

  const handleSalvarFotoCamera = useCallback(async () => {
    if (!cameraImageData) return

    try {
      setProcessandoArquivos(true)

      // Se não for múltiplo e já tiver um arquivo, deleta o anterior primeiro
      if (props.multiple === false && idsArquivos.length > 0) {
        try {
          await arquivoService.deletarArquivo(rotaUpload, idsArquivos[0])
        } catch (e) {
          console.error('Erro ao deletar arquivo anterior em modo single:', e)
        }
      }

      const formData = new FormData()
      const blob = dataURItoBlob(cameraImageData)
      formData.append('file', blob, `webcam-${new Date().getTime()}.jpg`)

      const idArquivo = await arquivoService.uploadArquivo(rotaUpload, formData)
      if (props.multiple === false) {
        setIdsArquivos([idArquivo])
      } else {
        setIdsArquivos((prevState) => [...prevState, idArquivo])
      }

      setCameraOpen(false)
      setCameraImageData(null)
    } catch (error) {
      console.error('Erro ao salvar foto da câmera:', error)
      if (error?.response?.status === 415) {
        setAlertaErroTipoArquivo(true)
      }
    } finally {
      setProcessandoArquivos(false)
    }
  }, [cameraImageData, rotaUpload, dataURItoBlob, props.multiple, idsArquivos])

  const handleResetFotoCamera = useCallback(() => {
    setCameraImageData(null)
  }, [])

  const handleCapturarFoto = useCallback(() => {
    if (webcamRef.current) {
      const imageSrc = webcamRef.current.getScreenshot()
      setCameraImageData(imageSrc)
    }
  }, [webcamRef])

  const handleDeletarArquivo = useCallback(async () => {
    if (!idArquivoDeletar) return

    try {
      setProcessandoArquivos(true)
      await arquivoService.deletarArquivo(rotaUpload, idArquivoDeletar)
      setIdsArquivos((prevState) => prevState.filter((id) => id !== idArquivoDeletar))
      handleFecharDeletar()
    } catch (error) {
      console.error('Erro ao deletar arquivo:', error)
    } finally {
      setProcessandoArquivos(false)
    }
  }, [idArquivoDeletar, rotaUpload, handleFecharDeletar])

  const definirTiposAceitos = useCallback((tiposArray) => {
    let accepted = ''
    tiposArray.forEach((tipo) => {
      switch (tipo) {
        case 'image':
          accepted += 'image/png,image/jpg,image/jpeg,'
          break
        case 'pdf':
          accepted += 'application/pdf,'
          break
        default:
          break
      }
    })
    return accepted
  }, [])

  const handleDropzoneClick = useCallback((event) => {
    if (event.target.tagName === 'BUTTON' || event.target.closest('button') || event.target.tagName === 'INPUT') {
      return
    }
    if (fileInputRef.current) {
      fileInputRef.current.click()
    }
  }, [])

  const handleCloseSnackbar = useCallback(() => {
    setAlertaErroTipoArquivo(false)
  }, [])

  return (
    <div>
      {processandoArquivos ? (
        <div className={classes.carregando}>
          <CircularProgress disableShrink />
        </div>
      ) : (
        <Grid alignItems="center" container style={{ justifyContent: center ? 'center' : 'flex-start' }}>
          {arquivos.length === 0 ? (
            <Grid item xs={12}>
              <div className={classes.dropzone} onClick={handleDropzoneClick}>
                <CloudUploadIcon className={classes.dropzoneIcon} />
                <Typography className={classes.dropzoneTitle} variant="h6">
                  Nenhum arquivo anexado
                </Typography>
                <Typography className={classes.dropzoneDescription} variant="body2">
                  Clique ou arraste e solte arquivos aqui para enviar
                </Typography>
                <div className={classes.buttonGroup}>
                  {tipos.includes('image') && (
                    <CustomButton
                      variant="outlined"
                      colorType="primary"
                      startIcon={<PhotoCamera />}
                      onClick={(e) => {
                        e.stopPropagation()
                        handleOpenCamera()
                      }}
                      disabled={disabledButton}
                    >
                      Usar Câmera
                    </CustomButton>
                  )}
                  <CustomButton variant="contained" colorType="primary" component="label" startIcon={<CloudUploadIcon />} disabled={disabledButton}>
                    Enviar arquivo
                    <input accept={definirTiposAceitos(tipos)} className={classes.enviarArquivoComputador} ref={fileInputRef} name="arquivos" type="file" hidden onChange={handleEnviarArquivoComputador} />
                  </CustomButton>
                </div>
              </div>
            </Grid>
          ) : (
            <>
              {arquivos.map((arquivo) => (
                <Grid
                  key={arquivo.id}
                  className={classes.arquivos}
                  item
                  md={props.multiple === false ? 12 : 3}
                  sm={props.multiple === false ? 12 : 6}
                  xs={12}
                  style={{
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                    justifyContent: 'center',
                  }}
                >
                  <img
                    src={getSrc(arquivo.data, arquivo.mimetype)}
                    width={arquivo.mimetype?.toLowerCase() !== 'image/png' && arquivo.mimetype?.toLowerCase() !== 'image/jpg' && arquivo.mimetype?.toLowerCase() !== 'image/jpeg' ? '60%' : '80%'}
                    style={props.multiple === false ? { maxWidth: '250px', marginBottom: '8px' } : {}}
                    alt=""
                  />
                  <Typography style={{ wordBreak: 'break-all', margin: '8px 0', textAlign: 'center' }}>{arquivo.nomeOriginal}</Typography>
                  <CustomButton variant="outlined" colorType="error" size="small" onClick={(e) => handleOpenDeletar(e, arquivo.id)} endIcon={<Close />}>
                    Remover
                  </CustomButton>
                </Grid>
              ))}

              {props.multiple !== false && (
                <Grid className={classes.margin} item md={12} sm={12} xs={12} style={{ display: 'flex', justifyContent: 'center' }}>
                  <div className={classes.buttonGroup} style={{ justifyContent: 'center', marginTop: '16px' }}>
                    {tipos.includes('image') && (
                      <CustomButton variant="outlined" colorType="primary" startIcon={<PhotoCamera />} onClick={handleOpenCamera} disabled={disabledButton}>
                        Usar Câmera
                      </CustomButton>
                    )}

                    <CustomButton variant="contained" colorType="primary" component="label" startIcon={<CloudUploadIcon />} disabled={disabledButton}>
                      Enviar arquivo
                      <input accept={definirTiposAceitos(tipos)} className={classes.enviarArquivoComputador} ref={fileInputRef} name="arquivos" type="file" hidden onChange={handleEnviarArquivoComputador} />
                    </CustomButton>
                  </div>
                </Grid>
              )}
            </>
          )}

          <Snackbar anchorOrigin={{ vertical: 'top', horizontal: 'right' }} open={alertaErroTipoArquivo} autoHideDuration={6000} onClose={handleCloseSnackbar}>
            <Alert onClose={handleCloseSnackbar} severity="error" variant="filled" style={{ width: '100%' }}>
              Tipo de arquivo não suportado. Selecione um arquivo compatível.
            </Alert>
          </Snackbar>

          <Modal
            aria-labelledby="transition-modal-title"
            aria-describedby="transition-modal-description"
            className={classes.modal}
            open={cameraOpen}
            onClose={handleCloseCamera}
            closeAfterTransition
            BackdropComponent={Backdrop}
            BackdropProps={{
              timeout: 500,
            }}
          >
            <Fade in={cameraOpen}>
              <div className={classes.paper}>
                {cameraImageData ? (
                  <div>
                    <div>
                      <img src={cameraImageData} width="100%" alt="" />
                    </div>
                    <div className={classes.fieldGroup} style={{ gap: '12px', marginTop: '16px' }}>
                      <CustomButton colorType="primary" variant="outlined" onClick={handleSalvarFotoCamera}>
                        Salvar foto
                      </CustomButton>
                      <CustomButton variant="outlined" onClick={handleResetFotoCamera}>
                        Refazer foto
                      </CustomButton>
                      <CustomButton colorType="primary" variant="outlined" onClick={handleCloseCamera}>
                        Fechar
                      </CustomButton>
                    </div>
                  </div>
                ) : (
                  <div>
                    <div>
                      <Webcam audio={false} height="100%" width="100%" minScreenshotWidth={1920} minScreenshotHeight={1080} ref={webcamRef} screenshotFormat="image/jpeg" videoConstraints={videoConstraints} />
                    </div>

                    <div style={{ marginTop: '16px', textAlign: 'center' }}>
                      <CustomButton colorType="primary" variant="outlined" onClick={handleCapturarFoto}>
                        Capturar foto
                      </CustomButton>
                    </div>
                  </div>
                )}
              </div>
            </Fade>
          </Modal>

          <Dialog open={deletarOpen} onClose={handleFecharDeletar} aria-labelledby="alert-dialog-title" aria-describedby="alert-dialog-description">
            <DialogContent>
              <DialogContentText id="alert-dialog-description">Tem certeza que deseja remover o arquivo?</DialogContentText>
            </DialogContent>
            <DialogActions>
              <CustomButton onClick={handleFecharDeletar} variant="outlined" colorType="primary">
                Não
              </CustomButton>
              <CustomButton disabled={processandoArquivos} onClick={handleDeletarArquivo} variant="contained" colorType="secondary" autoFocus>
                Sim
              </CustomButton>
            </DialogActions>
          </Dialog>
        </Grid>
      )}
    </div>
  )
}
